import merge from 'classnames';
import parseISO from 'date-fns/parseISO';
import find from 'lodash/find';

import { Lead } from '@/controllers/Lead';
import { Opportunity } from '@/controllers/Opportunity';
import { Order } from '@/controllers/Order';
import { PriceBreakDown, PriceBreakdownData, PriceBreakDownItemTypes } from '@/controllers/Product';
import { ActivityType, ContactVm } from '@/types';
import { formatDate } from '@/utils/format-date';
import { formatPhoneNumber } from '@/utils/format-phone-number';
import { removeNonNumeric } from '@/utils/remove-non-numeric';

import { Proposal } from '../types';

import {
  ActivityDetailsData,
  ActivityInstances,
  JobDetailsData,
  ProposalDetailsData,
} from './types';

class ActivityDetailsDataFormatter {
  getPriceBreakdown = (priceBreakdownData: PriceBreakdownData): PriceBreakDown => {
    const { equipmentPrice, servicesPrice, tax = 0, name } = priceBreakdownData;

    const priceBreakdown: PriceBreakDown = {
      items: [
        {
          code: 'EquipmentPrice',
          title: name,
          type: PriceBreakDownItemTypes.Product,
          price: equipmentPrice,
        },
      ],
      tax,
    };

    if (servicesPrice) {
      priceBreakdown.items.push({
        code: 'ServicesPrice',
        title: 'Service',
        type: PriceBreakDownItemTypes.Service,
        price: servicesPrice,
      });
    }

    return priceBreakdown;
  };

  getAddress = (contact: ContactVm): string => {
    const { addressLine1, addressLine2, city, state, zipCode } = contact;
    const byNonEmptyString = (str: string | null) => Boolean(str?.length);

    return [
      addressLine1,
      addressLine2,
      [city, merge(state, zipCode)].filter(byNonEmptyString).join(', '),
    ]
      .filter(byNonEmptyString)
      .map(line => `${line}\n`)
      .reduce((result: string, line: string) => `${result}${line}`, '');
  };

  getProposalDetailsDataFromLead(lead: Lead): ProposalDetailsData {
    const {
      id,
      firstName,
      lastName,
      email,
      phoneNumber,
      addressLine1,
      addressLine2,
      appointmentDate,
      city,
      state,
      zipCode,
      bucketId,
      photoAlbumLink,
      salesRepresentative,
      salesRepresentativeId,
      prospectNotes,
      disqualificationReason,
      mqlOneNotes,
      homeOwnerDetailsCardStatus,
      consideringReRoofing,
      reRoofTiming,
      isDoNotCall,
      isDoNotEmail,
      hasOptedOutOfEmail,
      rooferNotes,
      messageBoardId,
      salesforceBucketId,
      siteSurveyAsServiceInfo,
      watcherList,
    } = lead;

    const fullName = merge(firstName, lastName);

    return {
      activityId: id,
      activityType: ActivityType.Lead,
      fullName,
      contact: {
        contactId: '',
        firstName,
        lastName,
        email,
        phoneNumber: phoneNumber ? removeNonNumeric(phoneNumber).slice(-10) : '',
        addressLine1,
        addressLine2,
        appointmentDate: appointmentDate?.toString(),
        city,
        state,
        zipCode,
      },
      assignedSalesRepresentative: {
        fullName: merge(salesRepresentative.firstName, salesRepresentative.lastName),
        firstName: salesRepresentative.firstName,
        lastName: salesRepresentative.lastName,
        email: salesRepresentative.email,
        phone: salesRepresentative.phoneNumber,
        id: salesRepresentativeId,
      },
      bucketId,
      photoAlbumLink,
      attachments: [],
      leadInfo: {
        prospectNotes,
        disqualificationReason,
        mqlOneNotes,
        homeOwnerDetailsCardStatus,
        consideringReRoofing,
        reRoofTiming,
        isDoNotCall,
        isDoNotEmail,
        rooferNotes,
        hasOptedOutOfEmail,
        watcherList,
      },
      messageBoardId,
      salesforceBucketId: salesforceBucketId ?? '',
      siteSurveyAsServiceInfo: siteSurveyAsServiceInfo ?? null,
    };
  }

  getProposalDetailsDataFromOpportunity(opportunity: Opportunity): ProposalDetailsData {
    const {
      id,
      contact,
      bucketId,
      photoAlbumLink,
      salesRepresentativeEmail,
      salesRepresentativeFirstName,
      salesRepresentativeLastName,
      salesRepresentativePhone,
      salesRepresentativeId,
      messageBoardId,
      salesforceBucketId,
      siteSurveyAsServiceInfo,
    } = opportunity;
    const fullName = merge(contact.firstName, contact.lastName);

    return {
      activityId: id,
      activityType: ActivityType.Lead,
      opportunity,
      fullName,
      contact,
      assignedSalesRepresentative: {
        fullName: merge(salesRepresentativeFirstName, salesRepresentativeLastName),
        email: salesRepresentativeEmail,
        phone: salesRepresentativePhone,
        firstName: salesRepresentativeFirstName,
        lastName: salesRepresentativeLastName,
        id: salesRepresentativeId,
      },
      bucketId,
      photoAlbumLink,
      attachments: [],
      messageBoardId,
      salesforceBucketId: salesforceBucketId ?? '',
      siteSurveyAsServiceInfo: siteSurveyAsServiceInfo ?? null,
    };
  }

  getJobDetailsDataForDirectOrder(order: Order): JobDetailsData {
    const { shipmentContact, additionalContact, salesRepresentative } = order;
    const fullName = merge(shipmentContact?.firstName, shipmentContact?.lastName);
    const salesRepFullName = merge(salesRepresentative.firstName, salesRepresentative.lastName);

    return {
      fromOpportunity: false,
      activityId: order.id,
      activityType: ActivityType.Job,
      orderInfo: order,
      isDistribution: order.isDistribution,
      fullName,
      contact: shipmentContact as ContactVm,
      salesRepresentative: { fullName: salesRepFullName },
      priceBreakdown: this.getPriceBreakdown({
        name: order.productName,
        servicesPrice: order.servicesPrice,
        equipmentPrice: order.equipmentPrice,
        tax: order.tax,
      }),
      shippingAddress: {
        creationDate: formatDate(parseISO(order.createdOn)),
        deliveryType: order.orderDetails.deliveryType,
        name: fullName,
        phone: formatPhoneNumber(shipmentContact?.phoneNumber) ?? null,
        email: shipmentContact?.email ?? null,
        address: this.getAddress(shipmentContact as ContactVm),
      },
      additionalContactInfo: {
        contactType: additionalContact.contactType,
        name: merge(additionalContact.firstName, additionalContact.lastName),
        phone: formatPhoneNumber(additionalContact.phoneNumber),
      },
      deliveryInstruction: order.deliveryInstruction ?? '',
      photoAlbumLink: order.photoAlbumLink ?? '',
      bucketId: order.bucketId,
      attachments: [],
      orderItems: order.orderItems ?? [],
      messageBoardId: order.messageBoardId,
      siteSurveyAsServiceInfo: order.siteSurveyAsServiceInfo ?? null,
      salesforceBucketId: order.salesforceBucketId ?? '',
    };
  }

  getJobDetailsDataForDesignToOrder(opportunity: Opportunity, order: Order | null): JobDetailsData {
    const syncedProposal = find(opportunity.proposals, {
      id: opportunity.syncedProposalId,
    }) as Proposal;
    let jobDetails = {
      fromOpportunity: true,
      activityId: opportunity.id,
      activityType: ActivityType.Job,
      opportunity,
      contact: opportunity.contact,
      bucketId: opportunity.bucketId,
      photoAlbumLink: opportunity.photoAlbumLink,
      attachments: [],
      messageBoardId: opportunity.messageBoardId,
    } as JobDetailsData;

    const salesRepFullName = order
      ? merge(order.salesRepresentative.firstName, order.salesRepresentative.lastName)
      : merge(opportunity.salesRepresentativeFirstName, opportunity.salesRepresentativeLastName);

    jobDetails.salesRepresentative = {
      fullName: salesRepFullName,
    };

    if (order) {
      const { shipmentContact, additionalContact } = order;

      jobDetails = {
        ...jobDetails,
        activityId: order.id,
        orderInfo: order,
        shippingAddress: {
          creationDate: formatDate(parseISO(order.createdOn)),
          deliveryType: order.orderDetails.deliveryType,
          name: merge(shipmentContact?.firstName, shipmentContact?.lastName) ?? jobDetails.fullName,
          phone: formatPhoneNumber(shipmentContact?.phoneNumber) ?? null,
          email: shipmentContact?.email ?? null,
          address: this.getAddress(shipmentContact as ContactVm),
        },
        additionalContactInfo: {
          contactType: additionalContact.contactType,
          name: merge(additionalContact.firstName, additionalContact.lastName),
          phone: formatPhoneNumber(additionalContact.phoneNumber),
        },
        deliveryInstruction: order.deliveryInstruction ?? '',
        bucketId: order.bucketId,
        photoAlbumLink: order.photoAlbumLink ?? '',
        orderItems: order.orderItems ?? [],
        messageBoardId: order.messageBoardId ?? jobDetails.messageBoardId,
        isLeanForward: order.isLeanForward,
        leanForwardDaysCount: order.leanForwardDaysCount,
        leanForwardHomeOwnerGuarantee: order.leanForwardHomeOwnerGuarantee,
        isDistribution: order.isDistribution,
        siteSurveyAsServiceInfo: order.siteSurveyAsServiceInfo ?? null,
        salesforceBucketId: order.salesforceBucketId ?? '',
      };
    }

    if (syncedProposal === undefined) {
      return jobDetails;
    }

    const { equipmentPrice, servicesPrice, name, systemSize } = syncedProposal;

    jobDetails.proposalInfo = { name, systemSize };
    jobDetails.fullName = merge(opportunity.contact.firstName, opportunity.contact.lastName);
    jobDetails.priceBreakdown = this.getPriceBreakdown({
      equipmentPrice,
      servicesPrice,
      name,
    });

    return jobDetails;
  }

  format(activityInstances: ActivityInstances): ActivityDetailsData {
    const { lead, opportunity, order } = activityInstances;

    if (order) {
      return opportunity
        ? this.getJobDetailsDataForDesignToOrder(opportunity, order)
        : this.getJobDetailsDataForDirectOrder(order);
    }

    return opportunity
      ? this.getProposalDetailsDataFromOpportunity(opportunity)
      : this.getProposalDetailsDataFromLead(lead);
  }
}

export const activityDetailsDataFormatter = new ActivityDetailsDataFormatter();
